import { Suspense, lazy, useCallback, useState } from "react";
import "@/App.css";
import { PaintProvider, usePaint } from "./context/PaintContext";
import { HeroVideo } from "./components/HeroVideo";
import { Header, MobileCallBar } from "./components/Header";
import { Marquee, Services, Gallery, Reviews } from "./components/Sections";
import { Contact } from "./components/Contact";

// Split out so the opening screen paints before the WebGL tour, form and video tools download.
const Tour = lazy(() => import("./components/tour/Tour").then((m) => ({ default: m.Tour })));
const Estimate = lazy(() => import("./components/Estimate").then((m) => ({ default: m.Estimate })));
const SharedShortlist = lazy(() => import("./components/tour/SharedShortlist").then((m) => ({ default: m.SharedShortlist })));
const ExteriorSweep = lazy(() => import("./components/ExteriorSweep"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const PAGES = { "/privacy": PrivacyPage, "/terms": TermsPage };

const ExteriorTransition = () => {
  const { transition } = usePaint();
  if (!transition) return null;
  return (
    <Suspense fallback={null}>
      <ExteriorSweep />
    </Suspense>
  );
};

const Site = () => {
  const [revealed, setRevealed] = useState(false);
  const reveal = useCallback(() => setRevealed(true), []);

  return (
    <div id="top" className="sab-grain relative min-h-screen bg-beige text-charcoal">
      <a href="#tour" className="sr-only z-[120] bg-charcoal px-4 py-2 text-beige focus:not-sr-only focus:fixed focus:left-4 focus:top-4">Skip to content</a>
      <ExteriorTransition />
      {new URLSearchParams(window.location.search).has("shortlist") && (
        <Suspense fallback={null}>
          <SharedShortlist />
        </Suspense>
      )}
      <Header />
      <main>
        <HeroVideo onReveal={reveal} />
        <div className="relative z-0 -mt-[100vh]" data-testid="hero-reveal-content">
          <Suspense fallback={<section id="tour" className="min-h-screen" />}>
            <Tour ready={revealed} />
          </Suspense>
        </div>
        <Marquee />
        <Services />
        <Gallery />
        <Reviews />
        <Suspense fallback={<section id="estimate" className="min-h-[1100px]" />}>
          <Estimate />
        </Suspense>
      </main>
      <Contact />
      <MobileCallBar />
    </div>
  );
};

export default function App() {
  const Page = PAGES[window.location.pathname.replace(/\/+$/, "")];
  if (Page) {
    return (
      <Suspense fallback={null}>
        <Page />
      </Suspense>
    );
  }
  return (
    <PaintProvider>
      <Site />
    </PaintProvider>
  );
}
